import { Box, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import { FC, useCallback, useEffect, useState } from 'react'
import NoEntriesImage from '../../../../assets/images/no-entries.svg?react'
import {
  GetLocationDeltasParams,
  Location,
  LocationDeltaRecord,
  PagedDeltaRecords,
  PartialLocation,
  SafeWordDeltaRecord
} from '../../../../api/models'
import { TokenPaginator } from '../../../../components/TokenPaginator/TokenPaginator'
import { useLocationDeltas } from '../../../../api/apiQueries'
import { atom, useSetAtom } from 'jotai'
import { formatIsoTimestamp } from '../../../../utils/dateUtils'
import { MISSING_VALUE } from '../../../../constants'
import { TemplateIDNames, TemplateIDNameToValueMap } from '../locationManagementEnums'

interface FieldChange {
  field: string
  fieldDisplayName: string
  fromValue: string
  toValue: string
  isSafeword?: boolean
}

// Helper function to format field names for display
const formatFieldName = (field: string): string => {
  const fieldMap: Record<string, string> = {
    id: 'Location ID',
    name: 'Location Name',
    account: 'Base Station ID',
    uid: 'User ID',
    templateId: 'Template ID',
    dispatchNotes: 'Dispatch Notes',
    siteNumber: 'Site Number',
    // Address fields
    'address.street1': 'Street Address 1',
    'address.street2': 'Street Address 2',
    'address.city': 'City',
    'address.state': 'State',
    'address.zip': 'ZIP Code',
    'address.county': 'County',
    'address.residenceType': 'Property Type',
    'address.timeZone': 'Time Zone',
    // Contact fields
    'contacts.primaryContacts': 'Primary Contacts',
    'contacts.secondaryContacts': 'Secondary Contacts'
  }

  return fieldMap[field] || field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1')
}

const TEMPLATE_ID_VALUE_TO_NAME = new Map<number, TemplateIDNames>(
  (Object.entries(TemplateIDNameToValueMap) as [TemplateIDNames, number][]).map(([name, id]) => [id, name])
)

// resolve template id to friendly name or fallback to raw value
const formatTemplateId = (value: string | number): string => {
  const id = typeof value === 'number' ? value : Number(value)
  return (Number.isNaN(id) ? undefined : TEMPLATE_ID_VALUE_TO_NAME.get(id)) ?? String(value)
}

// Helper function to format values for display
const formatValue = (value: unknown, fieldPath?: string): string => {
  if (value === null || value === undefined) {
    return MISSING_VALUE
  }

  if (fieldPath === 'templateId' && (typeof value === 'string' || typeof value === 'number')) {
    return formatTemplateId(value)
  }

  if (typeof value === 'string') {
    return value
  }

  if (typeof value === 'number') {
    // Special handling for residence type enum
    if (fieldPath === 'address.residenceType') {
      const residenceTypes = ['Apartment', 'House', 'Commercial', 'Unknown']
      return residenceTypes[value] || `Unknown (${value})`
    }
    return String(value)
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return MISSING_VALUE
    return value
      .map(item => {
        if (typeof item === 'object' && item) {
          // Handle contact objects
          if ('name' in item && 'phone' in item) {
            const name =
              typeof item.name === 'object' && item.name && 'firstName' in item.name
                ? `${item.name.firstName || ''} ${item.name.lastName || ''}`.trim()
                : String(item.name || '')
            const phone =
              typeof item.phone === 'object' && item.phone && 'number' in item.phone ? String(item.phone.number || '') : String(item.phone || '')
            return `${name}${name && phone ? ' - ' : ''}${phone}`
          }
          // Handle other objects
          return JSON.stringify(item)
        }
        return String(item)
      })
      .join(', ')
  }

  return String(value)
}

// Deep comparison function to find differences between from and to objects
const getFieldChanges = (from: PartialLocation | undefined, to: PartialLocation | undefined): FieldChange[] => {
  const changes: FieldChange[] = []

  if (!from && !to) return changes

  const fromObj = from || {}
  const toObj = to || {}

  // Function to recursively compare objects and track field paths
  const compareFields = (fromValue: unknown, toValue: unknown, fieldPath: string = '') => {
    // Handle primitive values
    if (fromValue !== toValue && (!fromValue || !toValue || typeof fromValue !== 'object' || typeof toValue !== 'object')) {
      changes.push({
        field: fieldPath,
        fieldDisplayName: formatFieldName(fieldPath),
        fromValue: formatValue(fromValue, fieldPath),
        toValue: formatValue(toValue, fieldPath)
      })
      return
    }

    // Both are objects, compare their properties
    if (typeof fromValue === 'object' && typeof toValue === 'object' && fromValue !== null && toValue !== null) {
      const fromRecord = fromValue as Record<string, unknown>
      const toRecord = toValue as Record<string, unknown>

      const allKeys = new Set([...Object.keys(fromRecord), ...Object.keys(toRecord)])

      for (const key of allKeys) {
        const newPath = fieldPath ? `${fieldPath}.${key}` : key
        const fromProp = fromRecord[key]
        const toProp = toRecord[key]

        // Special handling for arrays (like contacts)
        if (Array.isArray(fromProp) || Array.isArray(toProp)) {
          // For arrays, do a simple comparison - if they're different, show the whole array change
          const fromArray = Array.isArray(fromProp) ? fromProp : []
          const toArray = Array.isArray(toProp) ? toProp : []

          if (JSON.stringify(fromArray) !== JSON.stringify(toArray)) {
            changes.push({
              field: newPath,
              fieldDisplayName: formatFieldName(newPath),
              fromValue: formatValue(fromArray, newPath),
              toValue: formatValue(toArray, newPath)
            })
          }
        } else {
          // Recursively compare objects
          compareFields(fromProp, toProp, newPath)
        }
      }
    }
  }

  // Get all possible keys from both objects
  const allKeys = new Set([...Object.keys(fromObj), ...Object.keys(toObj)])

  for (const key of allKeys) {
    const fromValue = fromObj[key as keyof PartialLocation]
    const toValue = toObj[key as keyof PartialLocation]
    compareFields(fromValue, toValue, key)
  }

  return changes
}

interface ChangelogProps {
  location: Location
}

const emptyResults: PagedDeltaRecords = { items: [] }
const initialPageTokens: string[] = []
const pageTokensAtom = atom<string[]>(initialPageTokens)

export const LocationChangeLogs: FC<ChangelogProps> = ({ location }) => {
  const [deltaParams, setDeltaParams] = useState<GetLocationDeltasParams>({ limit: 10 })

  // API call with pagination
  const [currentResults, setCurrentResults] = useState<PagedDeltaRecords>(emptyResults)
  const deltasQuery = useLocationDeltas(location.id, deltaParams)
  const pagedDeltaRecords: PagedDeltaRecords = deltasQuery.data ?? emptyResults

  useEffect(() => {
    if (pagedDeltaRecords !== emptyResults && pagedDeltaRecords !== currentResults) {
      // Previously suppressed `react-hooks/set-state-in-effect` here. Biome 1.9.4 has no equivalent rule, and this effect intentionally syncs paged results into local display state.
      setCurrentResults(pagedDeltaRecords)
    }
  }, [currentResults, pagedDeltaRecords])

  // Separate location and safeword delta records
  const locationDeltas: LocationDeltaRecord[] =
    currentResults.items?.filter((item): item is LocationDeltaRecord => 'from' in item && 'to' in item) ?? []

  const safewordDeltas: SafeWordDeltaRecord[] =
    currentResults.items?.filter((item): item is SafeWordDeltaRecord => item.resource === 'SafeWord') ?? []

  // Combine and sort all records by date (most recent first)
  const allRecords = [
    ...locationDeltas.map(delta => ({ ...delta, recordType: 'location' as const })),
    ...safewordDeltas.map(delta => ({ ...delta, recordType: 'safeword' as const }))
  ].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())

  const setPageTokens = useSetAtom(pageTokensAtom)

  // Clear pagination tokens whenever we leave the page
  useEffect(() => {
    return () => {
      setPageTokens([])
    }
  }, [setPageTokens])

  // Pagination click handler
  const onPaginatorClick = useCallback((nextToken?: string) => {
    if (nextToken) {
      setDeltaParams(prevState => ({ ...prevState, nextToken }))
    } else {
      // Remove nextToken from params
      setDeltaParams(prevState => {
        const { nextToken, ...rest } = prevState
        return rest
      })
    }
  }, [])

  return (
    <Paper data-testid={'changelog'}>
      <Typography variant="h2" p="var(--spacing-s)">
        Location Settings Change Log
      </Typography>
      <TableContainer sx={{ position: 'relative' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Last Updated</TableCell>
              <TableCell>What Was Changed</TableCell>
              <TableCell>Previous Value</TableCell>
              <TableCell>New Value</TableCell>
              <TableCell>Changed By</TableCell>
            </TableRow>
          </TableHead>
          <TableBody data-testid={'changelog-rows'}>
            {allRecords.map((record, recordIndex) => {
              if (record.recordType === 'location') {
                const locationDelta = record as LocationDeltaRecord & { recordType: 'location' }
                const changes = getFieldChanges(locationDelta.from, locationDelta.to)

                // If no changes detected, show a generic row
                if (changes.length === 0) {
                  return (
                    <TableRow
                      key={`location-${locationDelta.updatedAt}-${recordIndex}`}
                      sx={{
                        fontWeight: 400,
                        fontSize: 'var(--size-s)',
                        color: 'var(--text-primary)'
                      }}
                      className="changelog-table-row"
                      data-testid={`changelog-row-${recordIndex}`}
                    >
                      <TableCell
                        sx={{
                          fontWeight: 400,
                          fontSize: 'var(--size-s)',
                          color: 'var(--text-primary)'
                        }}
                      >
                        {formatIsoTimestamp(locationDelta.updatedAt)}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 400,
                          fontSize: 'var(--size-s)',
                          color: 'var(--text-primary)'
                        }}
                      >
                        Location Settings Updated
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 400,
                          fontSize: 'var(--size-s)',
                          color: 'var(--text-primary)'
                        }}
                      >
                        -
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 400,
                          fontSize: 'var(--size-s)',
                          color: 'var(--text-primary)'
                        }}
                      >
                        -
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 400,
                          fontSize: 'var(--size-s)',
                          color: 'var(--text-primary)'
                        }}
                      >
                        {locationDelta.updatedBy}
                      </TableCell>
                    </TableRow>
                  )
                }

                // Render location changes with proper alignment
                return (
                  <TableRow
                    key={`location-${locationDelta.updatedAt}-${recordIndex}`}
                    sx={{
                      fontWeight: 400,
                      fontSize: 'var(--size-s)',
                      color: 'var(--text-primary)'
                    }}
                    className="changelog-table-row"
                    data-testid={`changelog-row-${recordIndex}`}
                  >
                    <TableCell
                      sx={{
                        fontWeight: 400,
                        fontSize: 'var(--size-s)',
                        color: 'var(--text-primary)'
                      }}
                      data-testid={`changelog-row-${recordIndex}-updated-at`}
                    >
                      {formatIsoTimestamp(locationDelta.updatedAt)}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 400,
                        fontSize: 'var(--size-s)',
                        color: 'var(--text-primary)',
                        whiteSpace: 'pre'
                      }}
                      data-testid={`changelog-row-${recordIndex}-field-name`}
                    >
                      {changes.map(change => change.fieldDisplayName).join('\n')}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 400,
                        fontSize: 'var(--size-s)',
                        color: 'var(--text-primary)',
                        whiteSpace: 'pre-line'
                      }}
                      data-testid={`changelog-row-${recordIndex}-from-value`}
                    >
                      {changes.map(change => change.fromValue).join('\n')}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 400,
                        fontSize: 'var(--size-s)',
                        color: 'var(--text-primary)',
                        whiteSpace: 'pre-line'
                      }}
                      data-testid={`changelog-row-${recordIndex}-to-value`}
                    >
                      {changes.map(change => change.toValue).join('\n')}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 400,
                        fontSize: 'var(--size-s)',
                        color: 'var(--text-primary)'
                      }}
                      data-testid={`changelog-row-${recordIndex}-updated-by`}
                    >
                      {locationDelta.updatedBy}
                    </TableCell>
                  </TableRow>
                )
              } else {
                // Render safeword record
                const safewordDelta = record as SafeWordDeltaRecord & { recordType: 'safeword' }
                const recordKey = `safeword-${safewordDelta.updatedAt}-${recordIndex}`

                return (
                  <TableRow
                    key={recordKey}
                    sx={{
                      fontWeight: 400,
                      fontSize: 'var(--size-s)',
                      color: 'var(--text-primary)'
                    }}
                    className="changelog-table-row"
                    data-testid={`changelog-safeword-row-${recordIndex}`}
                  >
                    <TableCell
                      sx={{
                        fontWeight: 400,
                        fontSize: 'var(--size-s)',
                        color: 'var(--text-primary)'
                      }}
                    >
                      {formatIsoTimestamp(safewordDelta.updatedAt)}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 400,
                        fontSize: 'var(--size-s)',
                        color: 'var(--text-primary)'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>Safe Word</span>
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 400,
                        fontSize: 'var(--size-s)',
                        color: 'var(--text-primary)'
                      }}
                    >
                      ********
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 400,
                        fontSize: 'var(--size-s)',
                        color: 'var(--text-primary)'
                      }}
                    >
                      ********
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 400,
                        fontSize: 'var(--size-s)',
                        color: 'var(--text-primary)'
                      }}
                    >
                      {safewordDelta.updatedBy}
                    </TableCell>
                  </TableRow>
                )
              }
            })}
            {!deltasQuery.isLoading && allRecords.length === 0 && (
              <TableRow sx={{ height: '20.625rem' }}>
                <TableCell colSpan={5} align="center">
                  <NoEntriesImage />
                  <Box data-testid={'no-data-message'} sx={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>
                    No changes have been made to this location yet
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {deltasQuery.isLoading && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <CircularProgress />
          </Box>
        )}
        <TokenPaginator
          pageTokensAtom={pageTokensAtom}
          currentToken={deltaParams.nextToken}
          nextToken={currentResults.nextToken}
          onClick={onPaginatorClick}
        />
      </TableContainer>
    </Paper>
  )
}
